<div class="p-2xl flex flex-col gap-2xl" [formGroup]="notificationForm">
  @if (isLoading()) {
    <div class="flex items-center justify-center p-xl">
      <lib-loader size="lg" />
    </div>
  } @else {
    <!-- ── Preferences Settings ──────────────────────────────── -->
    <div class="flex flex-col gap-xl">
      <div class="flex flex-col gap-lg py-sm">
        <p
          class="text-neutral-900 font-semibold text-lg border-b border-neutral-200 py-lg">
          {{ 'preferences_settings_title' | transloco }}
        </p>
      </div>

      @for (type of notificationTypes(); track type.value) {
        @if (type.value !== NotificationTypeValue.SLA_TIME_NOTIFICATION) {
          <section class="flex flex-col gap-xl">
            <div class="flex items-center gap-lg py-sm">
              <div
                class="flex items-center justify-center w-5xl h-5xl rounded-lg flex-shrink-0 bg-primary-50">
                @switch (type.value) {
                  @case (NotificationTypeValue.REQUEST_UPDATED) {
                    <lib-notification-status-icon
                      iconClass="size-6"
                      [fill]="'var(--color-primary-500)'" />
                  }
                  @case (NotificationTypeValue.NEW_TASK_ASSIGNED) {
                    <lib-direct-box-reactive-icon
                      iconClass="size-6 text-primary-500"
                      [fill]="'var(--color-primary-500)'" />
                  }
                  @case (NotificationTypeValue.COLLABORATION) {
                    <lib-user-octagon-icon
                      iconClass="size-6 text-primary-500" />
                  }
                  @case (NotificationTypeValue.SLA_REACHED) {
                    <lib-tick-square-icon
                      iconClass="size-6 text-white"
                      [fill]="'var(--color-primary-500)'" />
                  }
                  @case (NotificationTypeValue.COMMENT_MENTION) {
                    <lib-mention-icon iconClass="size-6" />
                  }
                  @case (NotificationTypeValue.ON_HOLD) {
                    <lib-pending-icon
                      iconClass="size-7 text-white"
                      [fill]="'var(--color-primary-500)'" />
                  }
                  @case (NotificationTypeValue.RESUMED) {
                    <lib-resume-icon iconClass="size-7" />
                  }
                  @case (NotificationTypeValue.COMMENT_REACTION) {
                    <lib-reaction-icon iconClass="size-7" />
                  }
                  @case (NotificationTypeValue.CONTRIBUTOR_ADDED) {
                    <lib-user-two-icon
                      iconClass="size-7"
                      [fill]="'var(--color-primary-500)'" />
                  }
                  @case (NotificationTypeValue.CONTRIBUTOR_SUBMITTED_DATA) {
                    <lib-tick-square-icon
                      iconClass="size-6 text-white"
                      [fill]="'var(--color-primary-500)'" />
                  }
                  @case (NotificationTypeValue.DELEGATION_ACTIVATED) {
                    <lib-user-notification-icon
                      iconClass="size-7 text-primary-500"
                      [fill]="'var(--color-primary-500)'" />
                  }
                  @case (NotificationTypeValue.UPCOMING_DELEGATION) {
                    <lib-arrow-up-circle-icon
                      iconClass="size-7 text-primary-500"
                      [fill]="'var(--color-primary-500)'" />
                  }
                  @case (NotificationTypeValue.BORROWING_CREATED) {
                    <lib-money-add-icon
                      iconClass="size-7 text-primary-500"
                      [fill]="'var(--color-primary-500)'" />
                  }
                  @case (NotificationTypeValue.BORROWING_UPDATED) {
                    <lib-moneys-icon
                      iconClass="size-7 text-primary-500"
                      [fill]="'var(--color-primary-500)'" />
                  }
                  @case (NotificationTypeValue.TIME_DEPOSIT_CREATED) {
                    <lib-card-add-icon
                      iconClass="size-7 text-primary-500"
                      [fill]="'var(--color-primary-500)'" />
                  }
                  @case (NotificationTypeValue.TIME_DEPOSIT_UPDATED) {
                    <lib-card-edit-icon
                      iconClass="size-7 text-primary-500"
                      [fill]="'var(--color-primary-500)'" />
                  }
                }
              </div>

              <div class="flex-1 flex flex-col gap-x-sm">
                <p class="text-sm font-medium text-neutral-900 m-0">
                  {{ type.label }}
                </p>
                <p class="text-xs text-neutral-500 m-0">
                  {{ type.value + '_description_setting' | transloco }}
                </p>
              </div>

              <div class="flex-shrink-0 flex items-center gap-lg">
                @for (channel of allChannels; track channel.value) {
                  <lib-icon-button-toggle
                    [id]="type.value + '_' + channel.value"
                    [formGroup]="notificationForm"
                    [inputFormControlName]="type.value + '_' + channel.value"
                    [label]="channel.label"
                    [disabled]="
                      !!missingChannelsMap()[type.value]?.has(channel.value)
                    ">
                    @if (channel.value === NotificationChannelValue.IN_APP) {
                      <lib-monitor-icon iconClass="size-6"></lib-monitor-icon>
                    } @else {
                      <lib-mail-icon iconClass="size-6"></lib-mail-icon>
                    }
                  </lib-icon-button-toggle>
                }
              </div>
            </div>
          </section>
        }
      }
    </div>

    <!-- ── Reminders Settings ────────────────────────────────── -->
    <div class="flex flex-col gap-xl">
      <div class="flex flex-col gap-lg py-sm">
        <p
          class="text-lg font-semibold text-neutral-900 m-0 border-b border-neutral-200 py-lg">
          {{ 'reminders_settings_title' | transloco }}
        </p>
      </div>

      @for (type of notificationTypes(); track type.value) {
        @if (type.value === NotificationTypeValue.SLA_TIME_NOTIFICATION) {
          <section class="flex flex-col gap-xl">
            <!-- SLA Time Notification toggle row -->
            <div class="flex items-center gap-lg py-sm">
              <div
                class="flex items-center justify-center w-5xl h-5xl rounded-lg flex-shrink-0 bg-destructive-50">
                <lib-danger-icon iconClass="size-6 text-destructive-500" />
              </div>

              <div class="flex-1 flex flex-col gap-x-sm">
                <p class="text-sm font-medium text-neutral-900 m-0">
                  {{ type.label }}
                </p>
                <p class="text-xs text-neutral-500 m-0">
                  {{ type.value + '_description_setting' | transloco }}
                </p>
              </div>

              <div class="flex-shrink-0 flex items-center gap-lg">
                @for (channel of type.channels; track channel.value) {
                  <lib-icon-button-toggle
                    [id]="type.value + '_' + channel.value"
                    [formGroup]="notificationForm"
                    [inputFormControlName]="type.value + '_' + channel.value"
                    [label]="channel.label">
                    @if (channel.value === NotificationChannelValue.IN_APP) {
                      <lib-monitor-icon iconClass="size-6"></lib-monitor-icon>
                    } @else {
                      <lib-mail-icon iconClass="size-6"></lib-mail-icon>
                    }
                  </lib-icon-button-toggle>
                }
              </div>
            </div>

            <!-- SLA Notification Time Setting row (only when a channel is enabled) -->
            @if (type.has_preferences && hasAnySlaChannelEnabled(type.value)) {
              <div class="flex items-center gap-lg py-sm">
                <div
                  class="flex items-center justify-center w-5xl h-5xl rounded-lg flex-shrink-0 bg-primary-50">
                  <lib-clock-icon
                    iconClass="size-7"
                    [fill]="'var(--color-primary-500)'"
                    [stroke]="'var(--color-white)'" />
                </div>

                <div class="flex-1 flex flex-col gap-x-sm">
                  <p class="text-sm font-medium text-neutral-800 m-0">
                    {{
                      'sla_notification_time_setting_title_setting' | transloco
                    }}
                  </p>
                  <p class="text-xs text-neutral-500 m-0">
                    {{
                      'sla_notification_time_setting_description_setting'
                        | transloco
                    }}
                  </p>
                </div>

                <div class="flex-shrink-0 w-[22rem]">
                  <lib-select
                    [id]="type.value + '_preferences'"
                    inputClass="!h-6xl !text-sm"
                    [name]="type.value + '_preferences'"
                    [inputFormControlName]="type.value + '_preferences'"
                    [options]="slaTimeOptions()"
                    selectedKeyName="name" />
                </div>
              </div>
            }
          </section>
        }
      }
    </div>
  }
</div>
